/* eslint-disable react-hooks/exhaustive-deps */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormikProps, useFormik } from 'formik';

import useSurvey from 'hooks/useSurvey';
import { useUser } from 'hooks/useUser';

import getProspection from 'api/matchmaking/getProspection';

import { IDynamicValues, IUseControllerContextProps } from './types';

const useControllerContext = createContext<IUseControllerContextProps>(
  {} as IUseControllerContextProps,
);

interface IUseControllerProviderProps {
  children: ReactNode;
}

const UseSearchControllerProvider: React.FC<IUseControllerProviderProps> = ({ children }) => {
  const [isOpenModalFilters, setIsOpenModalFilters] = useState<boolean>(false);
  const [prospectionList, setProspectionList] = useState<IProspection[]>([]);
  const [prospectionListFiltered, setProspectionListFiltered] = useState<IProspection[]>([]);
  const [isProspectionLoading, setIsProspectionLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [nextPage, setNextPage] = useState<number | null>(null);

  const { user } = useUser();

  const { surveyQuestions } = useSurvey();

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const filterProspections = useCallback(
    async (prospections: IProspection[]) => {
      setProspectionListFiltered(
        prospections.filter(
          (prospection) =>
            prospection.fullname.toLowerCase().includes(searchText.toLowerCase()) ||
            prospection.companyFantasyName.toLowerCase().includes(searchText.toLowerCase()),
        ),
      );
    },
    [searchText],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const doRequest = useCallback(
    async (values: IDynamicValues, initialRequest?: boolean, page?: number) => {
      setIsProspectionLoading(true);

      try {
        const tags: string[] = [];

        Object.keys(values).forEach((tag: string) => {
          if (values[tag]) {
            const data = tag.split('-');

            data.forEach((tag) => {
              tags.push(tag);
            });
          }
        });

        const res = await getProspection({ tags, page: page || nextPage || 1 });

        setProspectionList((prev) => (initialRequest ? res.data : prev.concat(res.data)));
        setNextPage(res.nextPage || null);

        await filterProspections((prospectionList || []).concat(res.data));
      } finally {
        setIsProspectionLoading(false);
        setIsOpenModalFilters(false);
      }
    },
    [user],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const initialValues: IDynamicValues = {};

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => doRequest(values, true),
  });

  const { resetForm }: FormikProps<IDynamicValues> = formik;

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const cleanFilters = useCallback(() => {
    resetForm();
    setSearchText('');
    setProspectionList([]);
    setProspectionListFiltered([]);
    doRequest(initialValues);
  }, []);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      filterProspections(prospectionList);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchText, prospectionList, filterProspections]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({
      isLoading: isProspectionLoading,
      isOpenModalFilters,
      setIsOpenModalFilters,
      prospectionListFiltered,
      doRequest,
      formik,
      cleanFilters,
      searchText,
      setSearchText,
      surveyQuestions,
      nextPage,
    }),
    [
      isOpenModalFilters,
      setIsOpenModalFilters,
      prospectionListFiltered,
      doRequest,
      isProspectionLoading,
      formik,
      cleanFilters,
      searchText,
      setSearchText,
      surveyQuestions,
      nextPage,
    ],
  );

  return (
    <useControllerContext.Provider value={contextValue}>{children}</useControllerContext.Provider>
  );
};

const useSearchController = (): IUseControllerContextProps => {
  const context = useContext(useControllerContext);
  if (!context)
    throw new Error('useSearchController must be used within an UseSearchControllerProvider');
  return context;
};

export { UseSearchControllerProvider, useSearchController };
